import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, createTheme } from '@mui/material';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { InstantThemeProvider } from './contexts/InstantThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { RtlProvider } from './contexts/RtlProvider';
// import { SessionManager } from './utils/sessionManager';
import './index.css';
import './styles/instant-theme.css';
import { getAuth } from 'firebase/auth';
import DailyClosingReportsService from './services/dailyClosingReportsService';
import LoginPage from './pages/Login/LoginPage';
import Dashboard from './pages/Dashboard/ModernDashboard';
import HomePage from './pages/Home/HomePage';
import RestaurantPage from './pages/Restaurant/ModernRestaurantPage';
import OrdersPage from './pages/Orders/ModernOrdersPage';
import DeliveryPOSPage from './pages/Delivery/DeliveryPOSPage';
import PickupPanelPage from './pages/Delivery/PickupPanelPage';
import PaymentsPage from './pages/Payments/ModernPaymentsPage';
import ReportsPage from './pages/Reports/ModernReportsPage';
import DailyClosingReportsPage from './pages/DailyClosingReports/DailyClosingReportsPage';
import PrintPreviewPage from './pages/PrintPreview/PrintPreviewPage';
import SharedLayout from './components/SharedLayout';
import GlobalAlertProvider from './components/GlobalAlertProvider';
import SettingsPage from './pages/Settings/SettingsPage';
import ProgramSettingsPage from './pages/Settings/ProgramSettingsPage';
import UsersPermissionsPage from './pages/Settings/UsersPermissionsPage';
import PermissionsPage from './pages/Settings/PermissionsPage';
import UserManagementPage from './pages/Settings/UserManagementPage';
import DriverScreen from './pages/Drivers/DriverScreen';
import KitchenScreen from './pages/Kitchen/KitchenScreen';

// MUI Theme with border radius overrides
const theme = createTheme({
  shape: {
    borderRadius: 10, // Set global border radius
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
          '& fieldset': {
            borderRadius: '10px !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
        },
      },
    },
  },
});

// Protected Route Component - SECURITY GUARD
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('🔐 Starting authentication check...');
        
        // Check for existing session in localStorage
        const sessionData = localStorage.getItem('saheeh_pos_session');
        
        if (sessionData) {
          try {
            const session = JSON.parse(sessionData);
            const now = Date.now();
            
            // Check if session is expired
            if (session.expiresAt && session.expiresAt > now) {
              console.log('✅ Valid session found - user authenticated');
              setIsAuthenticated(true);
              setIsChecking(false);

              // Auto Z-Report: check if yesterday's report is missing and generate it
              try {
                const service = DailyClosingReportsService.getInstance();
                const result = await service.autoGenerateZReportIfMissing();
                if (result.generated) {
                  console.log(`📊 [AUTO Z-REPORT] Auto-generated Z-report for ${result.date}`);
                } else {
                  console.log(`📊 [AUTO Z-REPORT] ${result.reason}`);
                }
              } catch (e) {
                console.error('📊 [AUTO Z-REPORT] Error:', e);
              }

              return;
            } else {
              console.log('⏰ Session expired - clearing and redirecting to login');
              localStorage.removeItem('saheeh_pos_session');
            }
          } catch (parseError) {
            console.log('❌ Invalid session data - clearing and redirecting to login');
            localStorage.removeItem('saheeh_pos_session');
          }
        }
        
        console.log('❌ No session found - redirecting to login');
        setIsAuthenticated(false);
        setIsChecking(false);
      } catch (error) {
        console.error('❌ Authentication check failed:', error);
        setIsAuthenticated(false);
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading while checking authentication
  if (isChecking) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        fontFamily: '"Poppins", sans-serif',
        backgroundColor: '#ffffff',
        color: '#000000',
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}>
          <img
            src="/saheeh_logo_w.svg"
            alt="Saheeh"
            style={{
              width: '220px',
              marginBottom: '20px',
            }}
          />
          <h3 style={{ 
            color: '#000000', 
            marginBottom: '16px',
            fontSize: '1.5rem',
            fontWeight: 600,
          }}>
            🔐 Verifying Access...
          </h3>
          <p style={{ 
            color: '#666666', 
            fontSize: '14px',
          }}>
            Please wait while we verify your credentials
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log('❌ Not authenticated - redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Render protected content if authenticated
  console.log('✅ Authentication successful - rendering protected content');
  return <>{children}</>;
};

// Admin-only route — redirects non-admins to dashboard
const ADMIN_EMAILS = ['admin@saheehpos.com', 'admin@saheehpos.com', 'owner@saheehpos.com'];
const getLoggedInEmail = (): string => {
  // 1. Firebase Auth (most reliable — direct from auth state)
  try {
    const authUser = getAuth().currentUser;
    if (authUser?.email) return authUser.email.toLowerCase();
  } catch {}
  // 2. saheeh_pos_user
  try {
    const u = JSON.parse(localStorage.getItem('saheeh_pos_user') || '{}');
    if (u.email) return u.email.toLowerCase();
  } catch {}
  // 3. saheeh_pos_session
  try {
    const s = JSON.parse(localStorage.getItem('saheeh_pos_session') || '{}');
    if (s.user?.email) return s.user.email.toLowerCase();
  } catch {}
  return '';
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userEmail = getLoggedInEmail();
  if (!ADMIN_EMAILS.includes(userEmail)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

// Minimalist Coming Soon component - matches dark theme
const ComingSoon: React.FC<{ title: string; description?: string }> = ({ title, description }) => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '80vh',
      flexDirection: 'column',
      fontFamily: '"Poppins", sans-serif',
    }}>
      <div style={{
        backgroundColor: '#1e2139',
        border: '1px solid #2a2d3e',
        borderRadius: '16px',
        padding: '50px 40px',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
        maxWidth: '480px',
        width: '100%',
      }}>
        <img
          src="/saheeh_logo_w.svg"
          alt="Saheeh"
          style={{
            width: '260px',
            height: 'auto',
            marginBottom: '24px',
          }}
        />
        <h2 style={{
          color: '#ffffff',
          fontSize: '24px',
          fontWeight: 600,
          marginBottom: '12px',
          fontFamily: '"Poppins", sans-serif',
        }}>
          {title}
        </h2>
        <p style={{
          color: '#9ca3af',
          fontSize: '15px',
          fontFamily: '"Poppins", sans-serif',
          margin: 0,
        }}>
          {description || 'Demnächst verfügbar'}
        </p>
        <div style={{
          marginTop: '24px',
          padding: '10px 20px',
          backgroundColor: 'rgba(0, 200, 150, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(0, 200, 150, 0.2)',
          display: 'inline-block',
        }}>
          <span style={{ color: '#00C896', fontSize: '13px', fontWeight: 500 }}>
            In Entwicklung
          </span>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  // FORCE CLEAR any existing sessions on app startup for security
  useEffect(() => {
    console.log('🔐 App startup - clearing all sessions for security');
    // SessionManager.clearSession();
  }, []);

  return (
    <Provider store={store}>
        <LanguageProvider>
        <RtlProvider baseTheme={theme}>
        <InstantThemeProvider>
        <CssBaseline />
        <GlobalAlertProvider>
        <Router>
          <Routes>
            {/* PUBLIC ROUTES */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* PROTECTED ROUTES - All require authentication */}
            <Route path="/" element={
              <ProtectedRoute>
                <SharedLayout>
                  <HomePage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/home" element={
              <ProtectedRoute>
                <SharedLayout>
                  <HomePage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <SharedLayout>
                  <Dashboard />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/restaurant" element={
              <ProtectedRoute>
                <SharedLayout>
                  <RestaurantPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/restaurant-old" element={
              <ProtectedRoute>
                <SharedLayout>
                  <RestaurantPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute>
                <SharedLayout>
                  <OrdersPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/payments" element={
              <ProtectedRoute>
                <SharedLayout>
                  <PaymentsPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <SharedLayout>
                  <ReportsPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/daily-closing" element={
              <ProtectedRoute>
                <SharedLayout>
                  <DailyClosingReportsPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/print-preview" element={
              <ProtectedRoute>
                <SharedLayout>
                  <PrintPreviewPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/menu" element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            } />
            <Route path="/delivery" element={
              <ProtectedRoute>
                <SharedLayout>
                  <DeliveryPOSPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/cashdesk" element={
              <ProtectedRoute>
                <SharedLayout>
                  <DeliveryPOSPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/delivery/pickup-panel" element={
              <ProtectedRoute>
                <SharedLayout>
                  <PickupPanelPage />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/tables" element={
              <ProtectedRoute>
                <SharedLayout>
                  <ComingSoon title="Tischverwaltung" description="Tische, Reservierungen und Sitzplan verwalten" />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/kitchen" element={
              <ProtectedRoute>
                <SharedLayout>
                  <KitchenScreen />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/drivers" element={
              <ProtectedRoute>
                <SharedLayout>
                  <DriverScreen defaultView="map" />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/backoffice" element={
              <ProtectedRoute>
                <SharedLayout>
                  <ComingSoon title="Backoffice" description="Statistiken, Mitarbeiter und Geschäftsdaten verwalten" />
                </SharedLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <AdminRoute>
                  <SharedLayout>
                    <SettingsPage />
                  </SharedLayout>
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/settings/program" element={
              <ProtectedRoute>
                <AdminRoute>
                  <SharedLayout>
                    <ProgramSettingsPage />
                  </SharedLayout>
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/settings/program/users" element={
              <ProtectedRoute>
                <AdminRoute>
                  <SharedLayout>
                    <UsersPermissionsPage />
                  </SharedLayout>
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/settings/program/users/permissions" element={
              <ProtectedRoute>
                <AdminRoute>
                  <SharedLayout>
                    <PermissionsPage />
                  </SharedLayout>
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/settings/program/users/management" element={
              <ProtectedRoute>
                <AdminRoute>
                  <SharedLayout>
                    <UserManagementPage />
                  </SharedLayout>
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/shift" element={
              <ProtectedRoute>
                <SharedLayout>
                  <ComingSoon title="Schichtmanagement" description="Schichten und Arbeitszeiten verwalten" />
                </SharedLayout>
              </ProtectedRoute>
            } />

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
        </GlobalAlertProvider>
        </InstantThemeProvider>
        </RtlProvider>
        </LanguageProvider>
    </Provider>
  );
};

export default App;